import { MapPin, Star, Pencil } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { ConfirmDelete } from '@/components/ConfirmDelete';
import { toast } from '@/hooks/use-toast';
import { useDeletePickupAddress } from '@/hooksApi/usePickupAddress';
import { PickupAddress } from '@/types';

interface LocationRowProps {
  address: PickupAddress;
  onEdit: (uid: string) => void;
  onSetDefault: (address: PickupAddress) => void;
}

export const LocationRow = ({ address, onEdit, onSetDefault }: LocationRowProps) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { mutate: deleteAddress, isLoading: isDeleting } = useDeletePickupAddress();

  const handleDelete = () => {
    if (isDeleting) return;
    deleteAddress(address.uid, {
      onSuccess: () => {
        queryClient.invalidateQueries(['pickupAddresses']);
        toast({ title: t('address_removed') });
      },
    });
  };

  return (
    <div className='rounded-2xl border border-sd-border bg-sd-card shadow-sm'>
      <div className='p-5 flex items-start gap-4'>
        <div className='h-10 w-10 rounded-lg bg-sd-muted flex items-center justify-center shrink-0 mt-0.5'>
          <MapPin className='h-5 w-5 text-sd-muted-foreground' />
        </div>
        <div className='flex-1 min-w-0'>
          <div className='flex items-center gap-2 mb-1'>
            <h3 className='text-sm font-semibold text-sd-foreground m-0'>{address.title}</h3>
            {+address.default === 1 && (
              <span className='inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-sd-muted text-xs font-medium text-sd-muted-foreground'>
                <Star className='h-3 w-3 fill-current' />
                {t('default')}
              </span>
            )}
          </div>
          <p className='text-sm text-sd-muted-foreground m-0'>
            {address.region?.area_name && `${address.region.area_name}, `}
            {address.region?.province && `${address.region.province} · `}
            {address.address_text}
          </p>
        </div>
        <div className='flex items-center gap-2 shrink-0'>
          {+address.default !== 1 && (
            <button
              onClick={() => onSetDefault(address)}
              className='px-3 py-1.5 rounded-lg border border-sd-input bg-sd-background text-xs font-medium hover:bg-sd-muted transition-colors'
            >
              {t('set_as_default')}
            </button>
          )}
          <button
            onClick={() => onEdit(address.uid)}
            className='h-8 w-8 rounded-lg border border-sd-input bg-sd-background flex items-center justify-center hover:bg-sd-muted transition-colors'
          >
            <Pencil className='h-3.5 w-3.5 text-sd-muted-foreground' />
          </button>
          <ConfirmDelete
            title={t('delete_address')}
            description={`"${address.title}" ${t('will_be_permanently_deleted')}`}
            onConfirm={handleDelete}
            disabled={isDeleting}
          />
        </div>
      </div>
    </div>
  );
};
